import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Trophy, Gamepad2, MessageCircle, Camera, MapPin, Sparkles } from 'lucide-react';
import { projectSections, heroImages } from '../siteContent';

function Bullet({ children }) {
  return (
    <li className="flex gap-3 items-start">
      <span className="mt-1 h-6 w-6 grid place-items-center rounded-md bg-yellow-400/15 text-yellow-400 flex-shrink-0">
        <Check size={14} />
      </span>
      <span className="text-neutral-300 leading-relaxed">{children}</span>
    </li>
  );
}

function SectionTitle({ icon: Icon, eyebrow, title }) {
  return (
    <div className="flex flex-col items-start">
      <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-yellow-400/10 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
        <Icon size={14} />
        {eyebrow}
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">{title}</h2>
    </div>
  );
}

export default function NotreProjet() {
  const { objectif, classement, modes, messagerie, social } = projectSections;

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${heroImages.projetObjectif})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/60 to-neutral-950" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-28 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-medium tracking-wider uppercase">
            <Sparkles size={14} /> Notre projet
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-yellow-400 leading-[1.05]">
            {objectif.title}
          </h1>
          <p className="mt-6 max-w-3xl mx-auto text-neutral-300 text-lg leading-relaxed">
            {objectif.intro}
          </p>
        </div>
      </section>

      {/* Objectif */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle icon={Trophy} eyebrow="Objectif" title="Une expérience immersive et sans limite" />
            <ul className="mt-8 space-y-4">
              {objectif.bullets.map((b) => <Bullet key={b}>{b}</Bullet>)}
            </ul>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors">
              Nous contacter
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-yellow-400/10 blur-3xl rounded-3xl" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={heroImages.projetObjectif} alt="Présentation du projet BuzzBump" loading="lazy" className="w-full h-[460px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Classement */}
      <section className="bg-neutral-900/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <SectionTitle icon={Trophy} eyebrow="Récompenses" title={classement.title} />
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {classement.bullets.map((b, i) => (
              <div key={b} className="p-7 rounded-2xl border border-white/10 bg-neutral-950/60 hover:border-yellow-400/30 transition-colors">
                <div className="h-10 w-10 rounded-md bg-yellow-400/10 grid place-items-center text-yellow-400 mb-5 font-bold">{String(i + 1).padStart(2, '0')}</div>
                <p className="text-neutral-300 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modes de Jeu */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <SectionTitle icon={Gamepad2} eyebrow="Modes de jeu" title={modes.title} />
        <p className="mt-4 text-neutral-400 text-lg">{modes.subtitle}</p>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {modes.modes.map((m, i) => (
            <div key={m.name} className="relative p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-900/60 to-neutral-950 hover:border-yellow-400/30 transition-colors overflow-hidden">
              <div className="absolute top-0 right-0 h-32 w-32 bg-yellow-400/10 blur-3xl rounded-full" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/10 text-yellow-400 text-xs font-semibold mb-4">
                  Mode {i + 1}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{m.name}</h3>
                <p className="text-neutral-400 leading-relaxed">{m.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Messagerie */}
      <section className="bg-neutral-900/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 md:order-1">
            <div className="absolute -inset-6 bg-yellow-400/10 blur-3xl rounded-3xl" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={heroImages.projetMessagerie} alt="Messagerie intégrée BuzzBump" loading="lazy" className="w-full h-[460px] object-cover" />
            </div>
          </div>
          <div className="order-1 md:order-2">
            <SectionTitle icon={MessageCircle} eyebrow="Messagerie" title={messagerie.title} />
            <div className="mt-6 space-y-4 text-neutral-300 leading-relaxed whitespace-pre-line">
              {messagerie.text}
            </div>
            <h4 className="mt-8 text-xl font-semibold text-yellow-400">{messagerie.subtitle}</h4>
            <div className="mt-4 space-y-5">
              <div className="p-5 rounded-xl border border-white/10 bg-neutral-950/60">
                <div className="text-white font-semibold mb-2">Avec vos amis</div>
                <p className="text-neutral-400 leading-relaxed text-sm">{messagerie.friends}</p>
              </div>
              <div className="p-5 rounded-xl border border-white/10 bg-neutral-950/60">
                <div className="text-white font-semibold mb-2 flex items-center gap-2">
                  <MapPin size={16} className="text-yellow-400" /> Avec des inconnus
                </div>
                <p className="text-neutral-400 leading-relaxed text-sm">{messagerie.strangers}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Réseau Social */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle icon={Camera} eyebrow="Réseau social" title={social.title} />
            <div className="mt-6 space-y-4 text-neutral-300 leading-relaxed whitespace-pre-line">
              {social.text}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-yellow-400/10 blur-3xl rounded-3xl" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={heroImages.projetSocial} alt="Communauté BuzzBump engagée" loading="lazy" className="w-full h-[460px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-neutral-900 to-neutral-950 p-12 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white">
            Qu'attendez-vous pour rejoindre <span className="text-yellow-400">la ruche</span> ?
          </h2>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors">
            Nous contacter <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
